import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError, type EnrolmentFeed, type Program } from "@alight/contracts";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@alight/ui";
import { AlertTriangle, Check, Copy, KeyRound, Trash2 } from "lucide-react";
import { RowAction, RowActions } from "./RowActions";

/**
 * LINK REGISTRATIONS TO A SPREADSHEET — the end of exporting the same file
 * every week.
 *
 * The host's problem was never the export button; it was everything after it.
 * A downloaded CSV is a dead copy: the moment it lands, the columns they added
 * beside it, the totals, the highlighting and the tab it lived in all have to
 * be rebuilt, and the file is stale again by the next registration.
 *
 * A link inverts that. The spreadsheet fetches the data itself, so the host
 * builds their sheet ONCE and the registrations arrive into it. Google Sheets
 * re-reads an `IMPORTDATA` link on its own (roughly hourly, and whenever the
 * sheet is opened), so there is nothing for them to remember to do.
 *
 * WHAT THIS DIALOG HAS TO GET RIGHT, given who is using it:
 *  - the thing offered for copying is the FORMULA, not the url. Pasting a bare
 *    link into a spreadsheet cell produces a blue hyperlink and no data, which
 *    reads as "it didn't work" and is where a non-technical host gives up.
 *  - the instructions are the four clicks in Google Sheets, in order.
 *  - the security cost is stated in words, next to the link, permanently — not
 *    once at creation and never again. Whoever holds the link can read those
 *    families' details, so "Replace the key" has to be visible at the moment
 *    they realise they forwarded it.
 */

/** Radix forbids an empty SelectItem value, so "no program filter" needs one. */
const ALL_PROGRAMS = "__all__";

export function SheetLinkDialog({
  api,
  open,
  onOpenChange,
  programs,
}: {
  api: ReturnType<typeof apiClient>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every program, newest ordering — the same list the page's filter uses. */
  programs: Program[];
}) {
  const qc = useQueryClient();
  const [programId, setProgramId] = React.useState<string>(ALL_PROGRAMS);
  const [label, setLabel] = React.useState("");

  const feedsQuery = useQuery({
    queryKey: ["admin", "enrolment-feeds"],
    queryFn: () => api.adminListEnrolmentFeeds(),
    staleTime: 30 * 1000,
    retry: 1,
    enabled: open,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "enrolment-feeds"] });

  const create = useMutation({
    mutationFn: () =>
      api.adminCreateEnrolmentFeed({
        label: label.trim(),
        program_id: programId === ALL_PROGRAMS ? null : programId,
      }),
    onSuccess: () => {
      setLabel("");
      invalidate();
    },
  });

  const rotate = useMutation({
    mutationFn: (id: number) => api.adminRotateEnrolmentFeed(id),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.adminDeleteEnrolmentFeed(id),
    onSuccess: invalidate,
  });

  const feeds = feedsQuery.data ?? [];
  /* feedsQuery's error is in here too: a failed LIST otherwise renders as the
     cheerful "No links yet" empty state, and a host would go on to make a
     second link for a sheet that already has one. */
  const error = [feedsQuery.error, create.error, rotate.error, remove.error].find(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Link registrations to a spreadsheet</DialogTitle>
          <DialogDescription>
            Instead of exporting a file each time, give a Google Sheet its own link. It fetches
            the current registrations by itself — your own columns, formulas and formatting stay
            exactly where you put them.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-5 overflow-y-auto pr-1">
          {/* ---- make one ---- */}
          <div className="space-y-3 rounded-card border border-border bg-paper p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="feed-program">Which registrations</Label>
                <Select value={programId} onValueChange={setProgramId}>
                  <SelectTrigger id="feed-program">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_PROGRAMS}>Every program</SelectItem>
                    {programs.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="feed-label">Name it (optional)</Label>
                <Input
                  id="feed-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. My working sheet"
                  maxLength={80}
                />
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={() => create.mutate()}
              disabled={create.isPending}
            >
              {create.isPending ? "Making the link…" : "Make a link"}
            </Button>
          </div>

          {error ? (
            <p className="text-sm text-error">
              {error instanceof ApiError ? error.message : "Something went wrong. Try again."}
            </p>
          ) : null}

          {/* ---- what exists ---- */}
          {feedsQuery.isLoading ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : feedsQuery.isError ? null : feeds.length === 0 ? (
            <p className="text-sm text-muted">
              No links yet. Make one above and it will appear here whenever you need to copy it
              again.
            </p>
          ) : (
            <div className="space-y-3">
              {feeds.map((feed) => (
                <FeedCard
                  key={feed.id}
                  feed={feed}
                  onRotate={() => rotate.mutate(feed.id)}
                  onDelete={() => remove.mutate(feed.id)}
                  busy={
                    (rotate.isPending && rotate.variables === feed.id) ||
                    (remove.isPending && remove.variables === feed.id)
                  }
                />
              ))}
            </div>
          )}

          {/* ---- the honest bit ---- */}
          <div className="flex gap-2.5 rounded-card border border-warning/40 bg-warning/10 p-3 text-sm text-ink">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <p>
              A link works without signing in — anyone who has it can read those registrations,
              including parents&rsquo; contact details and anything the form asks about a child. Keep
              it in your own spreadsheet rather than emailing it around, and if it ever goes
              somewhere it shouldn&rsquo;t, use <strong>Replace the key</strong> to kill it instantly.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FeedCard({
  feed,
  onRotate,
  onDelete,
  busy,
}: {
  feed: EnrolmentFeed;
  onRotate: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  /* The formula, not the url. A bare link pasted into a cell becomes a
     hyperlink and imports nothing — see the note at the top of this file. */
  const formula = `=IMPORTDATA("${feed.url}")`;

  return (
    <div className="space-y-3 rounded-card border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-ink">{feed.label || "Registrations"}</span>
            <Badge variant={feed.program_id ? "accent" : "muted"}>
              {feed.program_title ?? (feed.program_id ? `Program #${feed.program_id}` : "Every program")}
            </Badge>
          </div>
          <p className="text-xs text-muted">
            {feed.last_used_at
              ? `Last read by a spreadsheet ${formatWhen(feed.last_used_at)}`
              : "Not read by a spreadsheet yet"}
          </p>
        </div>
        <RowActions label={`Options for ${feed.label || "this link"}`}>
          <RowAction
            icon={KeyRound}
            label="Replace the key"
            description="Stops the old link working immediately. Paste the new one into your sheet."
            onSelect={onRotate}
            disabled={busy}
          />
          <RowAction
            icon={Trash2}
            label="Delete this link"
            description="The sheet using it will stop updating. Cannot be undone."
            onSelect={onDelete}
            disabled={busy}
            destructive
          />
        </RowActions>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`feed-formula-${feed.id}`}>Paste this into a Google Sheet</Label>
        <div className="flex gap-2">
          <Input
            id={`feed-formula-${feed.id}`}
            readOnly
            value={formula}
            onFocus={(e) => e.currentTarget.select()}
            className="font-mono text-xs"
          />
          <CopyButton value={formula} />
        </div>
        <p className="text-xs text-muted">
          In Google Sheets: <strong>File → New → Spreadsheet</strong>, click the very first cell,
          paste, press Enter. The registrations fill in below it and refresh on their own.
        </p>
      </div>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      /* Clipboard access can be refused (an insecure origin, a locked-down
         browser). The field beside this button is selectable and selects itself
         on focus, so Ctrl+C still works — say nothing rather than show an error
         for something that has a perfectly good manual path. */
    }
  }

  return (
    <Button type="button" variant="outline" size="sm" onClick={copy} className="shrink-0">
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
